// The DM's wall controls over the board (issue 1144, slice 1; design/30 §1.1, §1.2). The canvas draws the walls and
// the chain being drawn and feeds the wall tool; this is where a selected wall's switches are set — blocks movement,
// blocks sight, a door and whether it is open — where it is deleted, and where every wall on the scene is cleared,
// asked first. Every change is one event through the table store; a player is offered none of it.
import { useEffect, useState } from 'react';
import type { PlacedLight, Wall } from '@dragons-table/vtt-shared';
import { ConfirmDialog } from '../shell/Dialogs';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { wallTool, type WallDraft } from './wallTool';
import { typingIn } from './typing';
import { pressWord } from '../shell/device';
import { BTN } from '../shell/ui';

const btn = BTN;

function useWallDraft(): WallDraft {
  const [d, setD] = useState(wallTool.get());
  useEffect(() => wallTool.subscribe(setD), []);
  return d;
}

/** What the tool commits becomes one event — the DM's own, never while looking through a player's eyes. */
/** The wall tool's hint: under a finger a tap is a corner and the End wall button ends the chain (a double-tap may too,
 *  but not every touch browser turns one into a double-click; no Shift, Enter or Escape). */
const wallHint = (drawing: boolean): string => {
  if (pressWord() === 'Tap') return drawing ? 'Tap each corner; End wall finishes it.' : 'Tap to start a wall, one tap a corner. Tap a wall to change it.';
  return drawing ? 'Click each corner; double-click or Enter ends the wall, Escape drops it.' : 'Click to start a wall, one click a corner (Shift: off the grid). Click a wall to change it.';
};

export function WallGate() {
  const { act, role, viewingAs } = useTableState();
  const mayAct = useMayAct();
  // The walls are the DM's board's (`role`), and drawing them the `map` power's (issue 1160).
  const dm = role === 'dm' && !viewingAs && mayAct('wall.add');
  useEffect(() => { if (dm) return undefined; wallTool.stop(); return wallTool.subscribe((d) => { if (d.on) wallTool.stop(); }); }, [dm]);
  useEffect(() => (dm ? wallTool.onCommit((c) => act(c)) : undefined), [dm, act]);
  return null;
}

function WallPanel({ wall }: { wall: Wall }) {
  const send = wallTool.send;
  const door = wall.door;
  return (
    <span className="flex flex-wrap items-center gap-2 rounded border border-stone-700 px-2 py-0.5" role="group" aria-label="The selected wall" data-testid="wall-panel">
      <label className="flex items-center gap-1">
        <input type="checkbox" checked={door ? !door.open : wall.move} disabled={!!door} onChange={(e) => send({ kind: 'wall.set', id: wall.id, move: e.target.checked })} /> Blocks movement
      </label>
      <label className="flex items-center gap-1">
        <input type="checkbox" checked={door ? !door.open : wall.sight} disabled={!!door} onChange={(e) => send({ kind: 'wall.set', id: wall.id, sight: e.target.checked })} /> Blocks sight
      </label>
      <label className="flex items-center gap-1">
        <input type="checkbox" checked={!!door} onChange={(e) => send({ kind: 'wall.set', id: wall.id, door: e.target.checked ? { open: false } : null })} /> Door
      </label>
      {door && <button type="button" className={btn} onClick={() => send({ kind: 'door.set', id: wall.id, open: !door.open })}>{door.open ? 'Close' : 'Open'}</button>}
      <button type="button" className={btn} onClick={() => { send({ kind: 'wall.remove', ids: [wall.id] }); wallTool.select(null); }}>Delete</button>
    </span>
  );
}

/** A picked placed light (issue 1145): its radii in squares, its switch, Move (the next click) and Delete. */
function LightPanel({ light, moving }: { light: PlacedLight; moving: boolean }) {
  const send = wallTool.send;
  const [bright, setBright] = useState(String(light.bright));
  const [dim, setDim] = useState(String(light.dim));
  useEffect(() => { setBright(String(light.bright)); setDim(String(light.dim)); }, [light.bright, light.dim]);
  const commit = () => {
    const b = Number(bright); const d = Number(dim);
    if (!Number.isFinite(b) || !Number.isFinite(d) || b < 0 || d <= 0 || b > d) { setBright(String(light.bright)); setDim(String(light.dim)); return; }
    if (b !== light.bright || d !== light.dim) send({ kind: 'light.set', light: { ...light, bright: b, dim: d } });
  };
  const field = 'w-12 rounded border border-stone-700 bg-stone-900 px-1';
  return (
    <span className="flex flex-wrap items-center gap-2 rounded border border-stone-700 px-2 py-0.5" role="group" aria-label="The selected light" data-testid="light-panel">
      <label className="flex items-center gap-1"><input type="checkbox" aria-label="Lit" checked={light.on} onChange={(e) => send({ kind: 'light.set', light: { ...light, on: e.target.checked } })} /> Lit</label>
      <input type="number" aria-label="Bright light, squares" className={field} min={0} step={0.5} value={bright} onChange={(e) => setBright(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} /> bright
      <input type="number" aria-label="Dim light, squares" className={field} min={0.5} step={0.5} value={dim} onChange={(e) => setDim(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} /> dim
      <button type="button" className={btn} aria-pressed={moving} onClick={() => wallTool.moveLight()}>{moving ? `${pressWord()} its new place` : 'Move'}</button>
      <button type="button" className={btn} onClick={() => { send({ kind: 'light.remove', ids: [light.id] }); wallTool.pickLight(null); }}>Delete</button>
    </span>
  );
}

export function WallComposer() {
  const { role, viewingAs, store } = useTableState();
  const mayAct = useMayAct();
  const d = useWallDraft();
  const [asking, setAsking] = useState(false);
  // Enter ends the chain, Escape drops it (and, with none, leaves the tool) — from anywhere, as the fog's Escape does.
  useEffect(() => {
    if (!d.on || asking) return undefined;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && d.chain.length && !typingIn(e.target)) { e.preventDefault(); wallTool.finish(); return; }
      if (e.key !== 'Escape') return;
      e.preventDefault();
      if (d.chain.length) wallTool.cancel(); else if (d.selected) wallTool.select(null); else if (d.light) wallTool.pickLight(null); else wallTool.stop();
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [d.on, d.chain.length, d.selected, d.light, asking]);
  if (!d.on || role !== 'dm' || viewingAs || !mayAct('wall.add')) return null;
  const walls = (store.state.walls ?? []) as Wall[]; // the DM's store: whole walls
  const selected = d.selected ? walls.find((w) => w.id === d.selected) ?? null : null;
  const lights = (store.state.lights ?? []) as PlacedLight[]; // the DM's store: whole lights
  const light = d.light ? lights.find((l) => l.id === d.light) ?? null : null;
  const modes = (
    <span className="flex items-center gap-1" role="group" aria-label="Draw walls or place lights">
      <button type="button" className={btn} aria-pressed={!d.lights} onClick={() => wallTool.mode(false)}>Walls</button>
      <button type="button" className={btn} aria-pressed={d.lights} onClick={() => wallTool.mode(true)}>Lights</button>
    </span>
  );
  if (d.lights) {
    return (
      <div className="pointer-events-auto flex max-w-2xl flex-wrap items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-2 text-xs text-stone-200" data-testid="wall-composer" role="group" aria-label="Lights">
        {modes}
        <span className="text-stone-400" data-testid="light-hint">{pressWord()} the board to place a light (a torch: 4 squares bright, 8 dim); {pressWord().toLowerCase()} a light to change it. They light a dim or dark scene with vision on.</span>
        <span className="text-stone-400" data-testid="light-count">{lights.length === 1 ? '1 light on this map' : `${lights.length} lights on this map`}</span>
        {light && <LightPanel light={light} moving={d.moving} />}
        <button type="button" className={btn} onClick={() => wallTool.stop()}>Done</button>
      </div>
    );
  }
  return (
    <div className="pointer-events-auto flex max-w-2xl flex-wrap items-center gap-2 self-start rounded bg-stone-900/95 px-3 py-2 text-xs text-stone-200" data-testid="wall-composer" role="group" aria-label="Walls">
      {modes}
      <span className="text-stone-400" data-testid="wall-hint">
        {wallHint(d.chain.length > 0)}
      </span>
      <span className="text-stone-400" data-testid="wall-count">{walls.length === 1 ? '1 wall on this map' : `${walls.length} walls on this map`}</span>
      {d.chain.length > 0 && (
        // Ends the chain as Enter does — the one way a touch screen is sure to have (a double-tap is up to the browser).
        <button type="button" className={btn} onClick={() => wallTool.finish()}>End wall</button>
      )}
      {selected && <WallPanel wall={selected} />}
      <button type="button" className={btn} disabled={!walls.length} onClick={() => setAsking(true)}>Clear all walls…</button>
      <button type="button" className={btn} title="Leave the wall tool (Escape). Until then a click on the board draws a wall instead of moving a token." onClick={() => wallTool.stop()}>Done</button>
      {asking && (
        <ConfirmDialog title="Clear all walls?" confirmLabel="Clear all walls" cancelLabel="Keep the walls" destructive
          body={<p>Every wall and door on this scene is removed. Tokens can then move anywhere on the map.</p>}
          onConfirm={() => { wallTool.send({ kind: 'wall.remove', ids: walls.map((w) => w.id) }); wallTool.select(null); setAsking(false); }} onClose={() => setAsking(false)} />
      )}
    </div>
  );
}
